import dayjs from "@/utils/dayjs";
import type { LocalStorageItemEnum } from "@/utils/enums/LocalStorageItemEnum";

import { jsonParse } from "../jsonHelper/jsonHelper";

export function getLocalStorageItem(item: LocalStorageItemEnum) {
  if (typeof localStorage === "undefined") return "";
  return localStorage.getItem(item);
}

export function setLocalStorageItem(item: LocalStorageItemEnum, value: any) {
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(item, value);
  }
}
export function removeItem(item: LocalStorageItemEnum) {
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(item);
  }
}

// Function to set an item in local storage with expiration time
export function setLocalStorageItemWithExpiry(
  key: LocalStorageItemEnum,
  value: any,
  expirationMs: number,
) {
  if (typeof localStorage === "undefined") return false;
  try {
    const expiry = dayjs().add(expirationMs, "millisecond").valueOf();
    const item = {
      value,
      expiry,
    };
    localStorage.setItem(key, JSON.stringify(item));
    return true;
  } catch (error) {
    return false;
  }
}

// Function to get an item from local storage, checking its expiration
export function getLocalStorageItemWithExpiry(key: LocalStorageItemEnum) {
  if (typeof localStorage === "undefined") return null;
  const itemStr = localStorage.getItem(key);
  if (!itemStr) {
    return null;
  }
  const item = JSON.parse(itemStr); // Replace jsonParse with JSON.parse
  const now = dayjs();
  if (now.isAfter(dayjs(item.expiry))) {
    // Use Day.js to check expiration
    localStorage.removeItem(key);
    return null;
  }
  return item.value;
}

interface FixRequireProperty {
  table_slug?: string;
  destination_id?: number;
  slug?: string | null;
}
interface KeepSearchHistoryInterface<T> {
  key: LocalStorageItemEnum;
  value: FixRequireProperty & T;
  maxLength?: number;
  checkKey?: (keyof T)[];
}

export const KeepSearchHistory = <T>({
  maxLength = 5,
  key,
  value,
  checkKey,
}: KeepSearchHistoryInterface<T>) => {
  try {
    if (typeof value !== "object") return;
    const getHistory = getLocalStorageItem(key);
    let toJson = null;
    // NOTE have local data
    if (getHistory) {
      toJson = JSON.parse(getHistory);
      const mergeData = [value, ...(toJson ?? [])];
      // NOTE remove last arr when data overflow max
      const uniqueKeys = [
        ...new Map(
          mergeData.map((item) => {
            const checkTableSlug = item.table_slug ? `-${item.table_slug}` : "";
            const checkOpt1 = `${item.destination_id}${checkTableSlug}`;
            const checkOpt2 = `${item.slug}${checkTableSlug}`;
            const checkOpt3Array = checkKey
              ? checkKey.map((keyItem) => `${item[keyItem]}`)
              : [];
            const checkOpt3 = checkOpt3Array.join("-");
            const checkId = value.destination_id ? checkOpt1 : checkOpt2;
            const selectOpt = checkOpt3 || checkId;
            return [selectOpt, item];
          }),
        ).values(),
      ];
      const isOverMax = uniqueKeys.length > maxLength;
      const removeLast = isOverMax ? uniqueKeys.slice(0, -1) : uniqueKeys;
      setLocalStorageItem(key, JSON.stringify(removeLast));
      return;
    }
    // NOTE not have local data
    setLocalStorageItem(key, JSON.stringify([value]));
  } catch (error) {
    throw new Error("keep history fail!");
  }
};

export const getHistory = (key: LocalStorageItemEnum): [] | null => {
  try {
    const history = getLocalStorageItem(key);
    if (!history) return null;
    return jsonParse(history);
  } catch (error) {
    return null;
  }
};
